import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchShopProducts } from "../lib/api";
import { useCart } from "../context/CartContext";
import SEO from "../components/SEO";

const CATS = [
  { id: "all", label: "All" },
  { id: "face", label: "Face" },
  { id: "cheeks", label: "Cheeks" },
  { id: "eyes", label: "Eyes" },
  { id: "lips", label: "Lips" },
];

export default function Shop() {
  const [data, setData] = useState({ products: [], bundles: [] });
  const [meta, setMeta] = useState({ shipping_flat: 5, free_shipping_threshold: 50 });
  const [activeCat, setActiveCat] = useState("all");
  const [loading, setLoading] = useState(true);
  const { addItem, count } = useCart();
  const [justAdded, setJustAdded] = useState(null);

  useEffect(() => {
    fetchShopProducts()
      .then((d) => {
        setData({ products: d.products, bundles: d.bundles });
        setMeta({
          shipping_flat: d.shipping_flat,
          free_shipping_threshold: d.free_shipping_threshold,
        });
      })
      .finally(() => setLoading(false));
  }, []);

  const filteredIndividuals = useMemo(
    () => (activeCat === "all" ? data.products : data.products.filter((p) => p.category === activeCat)),
    [data.products, activeCat]
  );

  const featuredBundle = useMemo(() => data.bundles.find((b) => b.id === "set-complete-30"), [data.bundles]);
  const otherBundles = useMemo(
    () => data.bundles.filter((b) => b.id !== "set-complete-30"),
    [data.bundles]
  );

  const handleAdd = (id) => {
    addItem(id);
    setJustAdded(id);
    setTimeout(() => setJustAdded(null), 1200);
  };

  return (
    <div data-testid="shop-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
      <SEO
        title="Shop — Curated Makeup Brushes & Bundles"
        description="Studio-grade makeup brushes, individually or as a set. Curated bundles for face, eyes, and lips. Free shipping over $50."
        path="/shop"
      />
      <header className="mb-12">
        <div className="overline mb-4">The Shop</div>
        <h1 className="font-serif-display text-5xl sm:text-6xl font-light text-stone-900 tracking-tight leading-[1.05]">
          Studio-grade brushes,
          <br />
          <em className="italic text-stone-700">individually or as a set.</em>
        </h1>
        <p className="mt-5 max-w-2xl text-base text-stone-600 leading-relaxed">
          Build your kit one brush at a time, or save with a curated set. Flat ${meta.shipping_flat.toFixed(0)} US shipping.
          Free over ${meta.free_shipping_threshold.toFixed(0)}.
        </p>
      </header>

      {/* ---------- Featured set ---------- */}
      {featuredBundle && (
        <section data-testid="featured-bundle" className="mb-16 lg:mb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 items-stretch bg-[#F2EFEB] border border-[#E5E0D8]">
            <div className="lg:col-span-7 relative overflow-hidden bg-white">
              <img
                src={featuredBundle.image}
                alt={featuredBundle.name}
                className="w-full h-[340px] lg:h-[520px] object-cover"
              />
              <div className="absolute top-5 left-5 bg-[#2C2A29] text-white text-[10px] uppercase tracking-[0.22em] px-3 py-1.5">
                Save ${featuredBundle.savings.toFixed(0)}
              </div>
            </div>
            <div className="lg:col-span-5 p-8 lg:p-10 flex flex-col">
              <div className="overline mb-3">Featured Set · {featuredBundle.included_count} Brushes</div>
              <h2 className="font-serif-display text-4xl sm:text-5xl text-stone-900 font-light leading-tight">
                {featuredBundle.name}
              </h2>
              <p className="mt-3 text-stone-600 italic">{featuredBundle.tagline}</p>
              <p className="mt-5 text-sm text-stone-700 leading-relaxed">{featuredBundle.description}</p>

              <div className="mt-6 flex items-baseline gap-3">
                <span className="font-serif-display text-4xl text-stone-900" data-testid={`bundle-price-${featuredBundle.id}`}>
                  ${featuredBundle.price.toFixed(2)}
                </span>
                <span className="text-sm text-stone-500 line-through">${featuredBundle.list_price.toFixed(2)}</span>
              </div>

              <div className="mt-auto pt-6 flex flex-wrap gap-3">
                <button
                  data-testid={`bundle-add-${featuredBundle.id}`}
                  onClick={() => handleAdd(featuredBundle.id)}
                  className={`text-xs uppercase tracking-[0.22em] px-7 py-4 transition-colors ${
                    justAdded === featuredBundle.id
                      ? "bg-[#2C2A29] text-white"
                      : "bg-[#8C6A5D] hover:bg-[#70544A] text-white"
                  }`}
                >
                  {justAdded === featuredBundle.id ? "Added ✓" : "Add Set to Cart"}
                </button>
                <Link
                  to="/catalog"
                  className="text-xs uppercase tracking-[0.22em] border border-[#8C6A5D] text-[#8C6A5D] hover:bg-white px-7 py-4 transition-colors"
                >
                  See What's Inside
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ---------- Smaller sets ---------- */}
      {otherBundles.length > 0 && (
        <section data-testid="other-bundles" className="mb-16 lg:mb-24">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="overline mb-3">Curated Sets</div>
              <h2 className="font-serif-display text-3xl sm:text-4xl text-stone-900 font-light">Smaller, focused kits.</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {otherBundles.map((b) => (
              <div
                key={b.id}
                data-testid={`bundle-card-${b.id}`}
                className="bg-white border border-[#E5E0D8] bb-card-hover flex flex-col"
              >
                <div className="aspect-[4/3] overflow-hidden bg-[#F2EFEB] relative">
                  <img src={b.image} alt={b.name} className="w-full h-full object-cover" />
                  <div className="absolute top-3 left-3 bg-[#2C2A29] text-white text-[10px] uppercase tracking-[0.22em] px-2.5 py-1">
                    Save ${b.savings.toFixed(0)}
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="overline mb-2">{b.included_count} Brushes</div>
                  <div className="font-serif-display text-2xl text-stone-900 leading-snug">{b.name}</div>
                  <p className="mt-2 text-sm text-stone-500 italic">{b.tagline}</p>
                  <div className="mt-auto pt-6 flex items-center justify-between gap-3">
                    <div className="flex items-baseline gap-2">
                      <span className="font-serif-display text-2xl text-stone-900" data-testid={`bundle-price-${b.id}`}>
                        ${b.price.toFixed(2)}
                      </span>
                      <span className="text-xs text-stone-500 line-through">${b.list_price.toFixed(2)}</span>
                    </div>
                    <button
                      data-testid={`bundle-add-${b.id}`}
                      onClick={() => handleAdd(b.id)}
                      className={`text-xs uppercase tracking-[0.22em] px-4 py-3 transition-colors ${
                        justAdded === b.id ? "bg-[#2C2A29] text-white" : "bg-[#8C6A5D] hover:bg-[#70544A] text-white"
                      }`}
                    >
                      {justAdded === b.id ? "Added ✓" : "Add"}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ---------- Individuals ---------- */}
      <section className="mb-12">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="overline mb-3">À La Carte</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-stone-900 font-light">Or build your own kit.</h2>
          </div>
          <Link
            to="/cart"
            data-testid="shop-go-to-cart"
            className="hidden md:inline-block text-xs uppercase tracking-[0.22em] text-[#8C6A5D] hover:text-[#70544A]"
          >
            Cart ({count}) →
          </Link>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-10 border-b border-[#E5E0D8] pb-4" data-testid="shop-filters">
          {CATS.map((c) => (
            <button
              key={c.id}
              data-testid={`shop-filter-${c.id}`}
              onClick={() => setActiveCat(c.id)}
              className={`text-xs uppercase tracking-[0.22em] px-4 py-2 transition-colors ${
                activeCat === c.id ? "bg-[#2C2A29] text-white" : "text-stone-500 hover:text-stone-900"
              }`}
            >
              {c.label}
            </button>
          ))}
          <span className="ml-auto text-xs text-stone-500">{filteredIndividuals.length} brushes</span>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-[#F2EFEB] h-[460px] animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12" data-testid="shop-grid">
            {filteredIndividuals.map((p, i) => (
              <div
                key={p.id}
                data-testid={`shop-card-${p.id}`}
                className="bg-white border border-[#E5E0D8] bb-card-hover flex flex-col"
              >
                <Link to={`/brush/${p.id}`} className="aspect-[4/5] overflow-hidden bg-[#F2EFEB] block">
                  <img src={p.image} alt={p.name} loading="lazy" decoding="async" className="w-full h-full object-cover hover:scale-[1.04] transition-transform duration-700" />
                </Link>
                <div className="p-6 flex flex-col flex-1">
                  <div className="overline mb-2">{p.category}</div>
                  <Link to={`/brush/${p.id}`} className="font-serif-display text-2xl text-stone-900 leading-snug hover:text-[#8C6A5D]">
                    {p.name}
                  </Link>
                  <p className="mt-2 text-sm text-stone-500 italic">{p.tagline}</p>
                  <div className="mt-auto pt-6 flex items-center justify-between">
                    <span className="font-serif-display text-2xl text-stone-900" data-testid={`shop-price-${p.id}`}>
                      ${p.price.toFixed(2)}
                    </span>
                    <button
                      data-testid={`shop-add-${p.id}`}
                      onClick={() => handleAdd(p.id)}
                      className={`text-xs uppercase tracking-[0.22em] px-5 py-3 transition-colors ${
                        justAdded === p.id ? "bg-[#2C2A29] text-white" : "bg-[#8C6A5D] hover:bg-[#70544A] text-white"
                      }`}
                    >
                      {justAdded === p.id ? "Added ✓" : "Add to Cart"}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
